import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Copy, Check, ShieldCheck, AlertTriangle } from "lucide-react";
import api, { apiError } from "./api";

const inputCls = "w-full bg-white border border-military/20 rounded-lg px-3 py-2.5 text-sm text-military-dark focus:outline-none focus:border-military";
const labelCls = "block text-[10px] uppercase tracking-[0.2em] font-semibold text-military-dark/50 mb-1.5";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CopyField = ({ value, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };
  return (
    <div className="flex items-center gap-2">
      <code data-testid={testid} className="flex-1 bg-offwhite border border-military/15 rounded-lg px-3 py-2.5 text-xs text-military-dark break-all">{value}</code>
      <button type="button" data-testid={`${testid}-copy`} onClick={copy} className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-military/25 px-3 py-2 text-[9px] uppercase tracking-widest font-semibold text-military-dark hover:bg-baby transition-colors">
        {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copiado" : "Copiar"}
      </button>
    </div>
  );
};

const CheckItem = ({ ok, label }) => (
  <li className="flex items-center gap-2 text-sm">
    <span className={`w-2.5 h-2.5 rounded-full ${ok ? "bg-military" : "bg-military-dark/20"}`} />
    <span className={ok ? "text-military-dark" : "text-military-dark/45"}>{label}</span>
  </li>
);

export default function Whatsapp() {
  const [cfg, setCfg] = useState(null);
  const [accessToken, setAccessToken] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  useEffect(() => {
    api.get("/admin/integrations/whatsapp").then((r) => setCfg(r.data)).catch((err) => toast.error(apiError(err)));
  }, []);

  if (!cfg) return <p className="text-military-dark/50 text-sm">Carregando...</p>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/integrations/whatsapp", {
        enabled: cfg.enabled,
        ai_enabled: cfg.ai_enabled,
        auto_orders: cfg.auto_orders,
        phone_number_id: cfg.phone_number_id,
        waba_id: cfg.waba_id,
        graph_version: cfg.graph_version,
        verify_token: cfg.verify_token,
        access_token: accessToken,
        app_secret: appSecret,
      });
      setCfg(data);
      setAccessToken("");
      setAppSecret("");
      toast.success("Configuração da integração salva");
    } catch (err) {
      toast.error(apiError(err));
    }
    setSaving(false);
  };

  const loadCatalog = async () => {
    setLoadingCatalog(true);
    try {
      const { data } = await api.get("/admin/integrations/whatsapp/catalog-preview");
      setCatalog(data.catalog);
    } catch (err) {
      toast.error(apiError(err));
    }
    setLoadingCatalog(false);
  };

  return (
    <div data-testid="admin-whatsapp">
      <h1 className="font-serif text-3xl md:text-4xl text-military-dark mb-2">Integração WhatsApp</h1>
      <p className="text-sm text-military-dark/50 mb-8 max-w-2xl">Estrutura pronta para a API oficial do WhatsApp Business (Meta) e para o futuro atendimento por IA. Nada é enviado ou cobrado enquanto a integração estiver desativada.</p>

      <div className="space-y-8 max-w-3xl">
        <section className="bg-white border border-military/10 rounded-lg p-6" data-testid="wa-status-section">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-military-dark/60">Status da conexão</p>
            <span data-testid="wa-status-badge" className={`text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 rounded-full ${cfg.enabled ? "bg-military/10 text-military" : "bg-amber-100 text-amber-700"}`}>
              {cfg.enabled ? "Ativa" : "Desativada"}
            </span>
          </div>
          <ul className="space-y-2.5">
            <CheckItem ok={!!cfg.phone_number_id} label={cfg.phone_number_id ? `Phone Number ID configurado (${cfg.phone_number_id})` : "Phone Number ID pendente"} />
            <CheckItem ok={cfg.has_access_token} label={cfg.has_access_token ? "Access Token salvo no servidor (nunca exibido)" : "Access Token pendente"} />
            <CheckItem ok={cfg.has_app_secret} label={cfg.has_app_secret ? "App Secret salvo no servidor (nunca exibido)" : "App Secret pendente"} />
            <CheckItem ok={!!cfg.verify_token} label="Token de verificação do webhook gerado" />
          </ul>
          <div className="mt-5 bg-baby/60 border border-military/10 rounded-lg p-4 flex gap-3">
            <ShieldCheck size={18} className="text-military-dark shrink-0 mt-0.5" />
            <p className="text-xs text-military-dark/70 leading-relaxed">As credenciais ficam somente no servidor da loja — nunca aparecem no navegador dos clientes. O registro manual de vendas pelo WhatsApp (aba Pedidos &amp; Vendas) continua funcionando normalmente, com ou sem a integração ativa.</p>
          </div>
        </section>

        <section className="bg-white border border-military/10 rounded-lg p-6" data-testid="wa-webhook-section">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-military-dark/60 mb-2">Webhook (para configurar no painel da Meta)</p>
          <p className="text-xs text-military-dark/50 mb-4">Quando for ativar, cole estes dois valores em Meta for Developers → WhatsApp → Configuração do webhook:</p>
          <div className="space-y-3">
            <div>
              <label className={labelCls}>URL de retorno (callback)</label>
              <CopyField testid="wa-webhook-url" value={`${BACKEND}/api/webhooks/whatsapp`} />
            </div>
            <div>
              <label className={labelCls}>Token de verificação</label>
              <CopyField testid="wa-verify-token" value={cfg.verify_token || "Gerando..."} />
            </div>
          </div>
        </section>

        <form onSubmit={save} className="space-y-8">
          <section className="bg-white border border-military/10 rounded-lg p-6" data-testid="wa-config-section">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-military-dark/60 mb-5">Credenciais da Meta (preencher quando for ativar)</p>
            <div className="space-y-4">
              <div><label className={labelCls}>Phone Number ID</label><input data-testid="wa-phone-id" value={cfg.phone_number_id} onChange={(e) => setCfg({ ...cfg, phone_number_id: e.target.value })} placeholder="Ex.: 123456789012345" className={inputCls} /></div>
              <div><label className={labelCls}>WhatsApp Business Account ID (opcional)</label><input data-testid="wa-waba-id" value={cfg.waba_id} onChange={(e) => setCfg({ ...cfg, waba_id: e.target.value })} placeholder="Ex.: 987654321098765" className={inputCls} /></div>
              <div><label className={labelCls}>Versão da Graph API</label><input data-testid="wa-graph-version" value={cfg.graph_version} onChange={(e) => setCfg({ ...cfg, graph_version: e.target.value })} placeholder="v26.0" className={inputCls} /></div>
              <div>
                <label className={labelCls}>Access Token {cfg.has_access_token && <span className="text-military">(salvo — cole um novo apenas para substituir)</span>}</label>
                <input data-testid="wa-access-token" type="password" autoComplete="new-password" value={accessToken} onChange={(e) => setAccessToken(e.target.value)} placeholder={cfg.has_access_token ? "••••••••••••••••" : "Cole aqui quando obtiver na Meta"} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>App Secret {cfg.has_app_secret && <span className="text-military">(salvo — cole um novo apenas para substituir)</span>}</label>
                <input data-testid="wa-app-secret" type="password" autoComplete="new-password" value={appSecret} onChange={(e) => setAppSecret(e.target.value)} placeholder={cfg.has_app_secret ? "••••••••••••••••" : "Cole aqui quando obtiver na Meta"} className={inputCls} />
              </div>
            </div>
          </section>

          <section className="bg-white border border-military/10 rounded-lg p-6" data-testid="wa-toggles-section">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-military-dark/60 mb-5">Ativação</p>
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm text-military-dark cursor-pointer">
                <input data-testid="wa-enabled" type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="w-4 h-4 accent-[#4E5D46]" />
                Ativar integração com a API do WhatsApp (só após configurar as credenciais da Meta)
              </label>
              <label className="flex items-center gap-2 text-sm text-military-dark cursor-pointer">
                <input data-testid="wa-ai-enabled" type="checkbox" checked={!!cfg.ai_enabled} onChange={(e) => setCfg({ ...cfg, ai_enabled: e.target.checked })} className="w-4 h-4 accent-[#4E5D46]" />
                Atendimento automático por IA (futuro — requer integração ativa)
              </label>
              <label className="flex items-center gap-2 text-sm text-military-dark cursor-pointer">
                <input data-testid="wa-auto-orders" type="checkbox" checked={!!cfg.auto_orders} onChange={(e) => setCfg({ ...cfg, auto_orders: e.target.checked })} className="w-4 h-4 accent-[#4E5D46]" />
                Registro automático de pedidos pela IA (futuro — requer integração ativa)
              </label>
            </div>
            <div className="mt-5 bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
              <AlertTriangle size={18} className="text-amber-700 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-relaxed">Deixe tudo desativado até concluir a configuração na Meta. Mesmo ativada, a integração não cobra nada: custos da API do WhatsApp dependem do seu cadastro e uso na Meta.</p>
            </div>
          </section>

          <button data-testid="wa-save-button" type="submit" disabled={saving} className="rounded-full bg-military text-offwhite px-8 py-3.5 text-xs uppercase tracking-widest font-semibold hover:bg-military-dark transition-colors disabled:opacity-50">
            {saving ? "Salvando..." : "Salvar configuração"}
          </button>
        </form>

        <section className="bg-white border border-military/10 rounded-lg p-6" data-testid="wa-catalog-section">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-military-dark/60 mb-2">O que o futuro assistente de IA enxergará</p>
          <p className="text-xs text-military-dark/50 mb-4">Produtos, preços, sabores e estoque em tempo real — usados para responder clientes e montar pedidos.</p>
          <button type="button" data-testid="wa-catalog-preview-button" onClick={loadCatalog} disabled={loadingCatalog} className="rounded-full border border-military/25 px-5 py-2.5 text-[10px] uppercase tracking-widest font-semibold text-military-dark hover:bg-baby transition-colors disabled:opacity-50">
            {loadingCatalog ? "Carregando..." : "Visualizar contexto do catálogo"}
          </button>
          {catalog && <pre data-testid="wa-catalog-preview" className="mt-4 bg-offwhite border border-military/15 rounded-lg p-4 text-xs text-military-dark/80 whitespace-pre-wrap leading-relaxed max-h-72 overflow-y-auto">{catalog}</pre>}
        </section>

        <section className="bg-white border border-military/10 rounded-lg p-6" data-testid="wa-steps-section">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-military-dark/60 mb-5">Como ativar no futuro (passo a passo)</p>
          <ol className="space-y-3 text-sm text-military-dark/70 leading-relaxed list-decimal list-inside">
            <li>Crie ou acesse um app em <span className="font-semibold text-military-dark">developers.facebook.com/apps</span> e adicione o produto WhatsApp.</li>
            <li>Em Configurações → Básico, copie o <span className="font-semibold text-military-dark">App Secret</span>; em WhatsApp → Configuração da API, copie o <span className="font-semibold text-military-dark">Phone Number ID</span>.</li>
            <li>Crie um System User nas configurações do negócio, associe o app e a conta do WhatsApp Business, e gere um <span className="font-semibold text-military-dark">token permanente</span> com a permissão whatsapp_business_messaging.</li>
            <li>Preencha as credenciais acima e salve.</li>
            <li>No painel da Meta, configure o webhook com a URL e o token de verificação exibidos nesta página, assinando o campo <span className="font-semibold text-military-dark">messages</span>.</li>
            <li>Marque "Ativar integração", salve e faça um envio de teste. Depois disso, o atendimento por IA e os pedidos automáticos podem ser ligados aqui mesmo.</li>
          </ol>
        </section>

        <section className="bg-white border border-military/10 rounded-lg p-6" data-testid="wa-ready-section">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-military-dark/60 mb-5">O que já está pronto na loja</p>
          <ul className="space-y-2.5 text-sm text-military-dark/70">
            <li className="flex gap-2"><MessageCircle size={15} className="text-military shrink-0 mt-0.5" /> Webhook seguro com verificação da Meta e validação de assinatura nas mensagens recebidas.</li>
            <li className="flex gap-2"><MessageCircle size={15} className="text-military shrink-0 mt-0.5" /> Proteção contra mensagens e pedidos duplicados.</li>
            <li className="flex gap-2"><MessageCircle size={15} className="text-military shrink-0 mt-0.5" /> Pedidos vindos do WhatsApp aparecem em Pedidos &amp; Vendas e o estoque só é baixado quando você confirma o pagamento.</li>
            <li className="flex gap-2"><MessageCircle size={15} className="text-military shrink-0 mt-0.5" /> Contexto de catálogo com preços, sabores e estoque pronto para a IA.</li>
            <li className="flex gap-2"><MessageCircle size={15} className="text-military shrink-0 mt-0.5" /> Estrutura para envio de mensagens e para o futuro link de pagamento (Sicoob).</li>
          </ul>
        </section>
      </div>
    </div>
  );
}
